import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Eye, Loader2, AlertCircle } from 'lucide-react';
import VideoPlayer from '../ui/VideoPlayer';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../config/api';

const SharedAnalysisDetail = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const { getIdToken } = useAuth();
  
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formattedAnalysis, setFormattedAnalysis] = useState(null);
  
  useEffect(() => {
    fetchSharedAnalysis();
  }, [shareId]);
  
  const fetchSharedAnalysis = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = await getIdToken();
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // First fetch the shared analysis metadata
      const response = await fetch(`${API_BASE_URL}/shared-analyses/${shareId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setAnalysis(data);
      
      // Then fetch the formatted content if we have the analysis ID
      if (data.analysis_id) {
        await fetchFormattedAnalysis(data.analysis_id, token);
      }
    } catch (err) {
      console.error('Error fetching shared analysis:', err);
      setError('Failed to load shared analysis: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchFormattedAnalysis = async (analysisId, token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/analyses/${analysisId}/formatted`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch formatted analysis: ${response.statusText}`);
      }
      
      const data = await response.json();
      setFormattedAnalysis(data.html || analysis.text);
    } catch (err) {
      console.error('Error fetching formatted analysis:', err);
      // Fallback to the original analysis text
      setFormattedAnalysis(analysis?.text);
    }
  };
  
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    if (timestamp.seconds) {
      const date = new Date(timestamp.seconds * 1000);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    }
    
    return new Date(timestamp).toLocaleDateString();
  };
  
  const handleDownloadPDF = async () => {
    try {
      setLoading(true);
      
      const token = await getIdToken();
      if (!token) throw new Error('Authentication required');
      
      // Create a loading indicator
      const loadingIndicator = document.createElement('div');
      loadingIndicator.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
      loadingIndicator.innerHTML = `
        <div class="bg-white p-6 rounded-lg shadow-lg">
          <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p class="text-center text-lg font-medium">Generating PDF...</p>
          <p class="text-center text-sm text-gray-500">This may take a few moments</p>
        </div>
      `;
      document.body.appendChild(loadingIndicator);
      
      // Request the PDF from the server with appropriate authentication
      const response = await fetch(`${API_BASE_URL}/analyses/${analysis.analysis_id}/pdf`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to generate PDF: ${response.statusText}`);
      }
      
      // Get the PDF as a blob
      const blob = await response.blob();
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a link to download the PDF
      const link = document.createElement('a');
      link.href = url;
      link.download = `dance_analysis_${analysis.dance_type || 'report'}.pdf`;
      
      // Append link to body, click it, and remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(loadingIndicator);
      setLoading(false);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setError(`Failed to download PDF: ${err.message}`);
      
      // Remove loading indicator if there was an error
      const loadingIndicator = document.querySelector('.fixed.inset-0.bg-black.bg-opacity-50');
      if (loadingIndicator) {
        document.body.removeChild(loadingIndicator);
      }
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-4" />
        <p className="text-gray-600">Loading shared analysis...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="h-full">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
          <span>{error}</span>
        </div>
        <button
          onClick={() => navigate('/shared-analyses')}
          className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Shared Analyses
        </button>
      </div>
    );
  }
  
  if (!analysis) {
    return (
      <div className="h-full">
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
          Analysis not found or access denied.
        </div>
        <button
          onClick={() => navigate('/shared-analyses')}
          className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Shared Analyses
        </button>
      </div>
    );
  }

  return (
    <div className="h-full">
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => navigate('/shared-analyses')}
          className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Shared Analyses
        </button>
        
        <div className="flex gap-2">
          <button
            onClick={handleDownloadPDF}
            className="flex items-center px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
            title="Download PDF"
            disabled={loading}
          >
            <Download className="w-4 h-4 mr-1" />
            Download PDF
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-blue-800 text-white p-6">
          <h1 className="text-2xl font-bold mb-2">Shared Dance Analysis</h1>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mt-4">
            <div>
              <p className="opacity-75 mb-1">Dance Type:</p>
              <p className="font-medium">{analysis.dance_type}</p>
            </div>
            
            <div>
              <p className="opacity-75 mb-1">Dancers:</p>
              <p className="font-medium">{analysis.dancers}</p>
            </div>
            
            <div>
              <p className="opacity-75 mb-1">Shared By:</p>
              <p className="font-medium">{analysis.owner_email}</p>
            </div>
            
            <div>
              <p className="opacity-75 mb-1">Date Shared:</p>
              <p className="font-medium">{formatDate(analysis.shared_at)}</p>
            </div>
            
            {analysis.analysis_type && (
              <div>
                <p className="opacity-75 mb-1">Analysis Type:</p>
                <p className="font-medium capitalize">
                  {analysis.analysis_type === 'solo' 
                    ? 'Solo Analysis' 
                    : analysis.analysis_type === 'duo'
                      ? 'Duo Analysis'
                      : analysis.analysis_type === 'formation'
                        ? 'Formation Analysis'
                        : 'Couple Analysis'}
                </p>
              </div>
            )}
            
            {/* Add Total Score display */}
            {analysis.total_score && (
              <div>
                <p className="opacity-75 mb-1">Total Score:</p>
                <p className="font-medium text-yellow-300 text-xl">
                  {analysis.total_score}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Video Section */}
        {analysis.video_url && (
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold mb-4 text-gray-700">Performance Video</h2>
            <div className="aspect-video">
              <VideoPlayer 
                videoUrl={analysis.video_url} 
                className="w-full h-full rounded-lg shadow"
              />
            </div>
          </div>
        )}

        {/* Analysis Content */}
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-700">Analysis</h2>
          
          <div
            className="prose max-w-none"
            dangerouslySetInnerHTML={{
              __html: formattedAnalysis || analysis.text || 'No analysis content available.',
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default SharedAnalysisDetail;